import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiPhone, FiMail, FiArrowRight, FiLoader } from "react-icons/fi";
import dashboardService from "../services/dashboardService";
import voiceService from "../services/voiceService";

function pickNumber(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function normalizeStatsPayload(data) {
  if (!data || typeof data !== "object") return { voice: null, email: null };
  return {
    voice: pickNumber(
      data.voiceSuccessCount ??
        data.voiceSuccessCount ??
        data.voiceTests ??
        data.voice_count,
    ),
    email: pickNumber(
      data.emailSuccessCount ??
        data.emailSuccessCount ??
        data.emailTests ??
        data.email_count,
    ),
  };
}

function extractHistoryList(res) {
  if (!res || !res.success) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.history)) return d.history;
  if (Array.isArray(d?.calls)) return d.calls;
  if (Array.isArray(d?.items)) return d.items;
  return [];
}

function countsFromHistory(list) {
  let voice = 0;
  let email = 0;
  for (const row of list) {
    const c = String(
      row.channel || row.type || row.medium || row.deliveryChannel || "",
    ).toLowerCase();
    if (c === "email" || c === "e-mail") email += 1;
    else voice += 1;
  }
  return { voice, email };
}

const Dashboard = () => {
  const [voiceCount, setVoiceCount] = useState(null);
  const [emailCount, setEmailCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      let v = null;
      let e = null;

      try {
        const statsRes = await dashboardService.getStats();
        if (!cancelled && statsRes && statsRes.success && statsRes.data) {
          const n = normalizeStatsPayload(statsRes.data);
          v = n.voice;
          e = n.email;
        }
      } catch (err) {
        console.warn("Dashboard stats unavailable:", err);
      }

      if (!cancelled) {
        setVoiceCount(v);
        setEmailCount(e);
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const formatCount = (n) => {
    if (loading) return "…";
    if (n === null || n === undefined) return "—";
    return n;
  };

  return (
    <div>
      <h1 style={{ marginBottom: "0.5rem", color: "#2c3e50" }}>Dashboard</h1>
      <p style={{ marginBottom: "2rem", color: "#7f8c8d", maxWidth: "640px" }}>
        Overview of outbound tests tied to your account. Use dedicated pages to
        run voice or email tests without cluttering this screen.
      </p>

      {error && (
        <div
          style={{
            background: "#fdf2f2",
            color: "#c0392b",
            padding: "0.75rem 1rem",
            borderRadius: "5px",
            marginBottom: "1.5rem",
            fontSize: "0.95rem",
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: "1.25rem",
          marginBottom: "2.5rem",
        }}
      >
        <div className="dashboard-card" style={{ marginBottom: 0 }}>
          <div className="card-header">
            <h3 className="card-title">Voice tests</h3>
            <FiPhone className="card-icon" style={{ color: "#27ae60" }} />
          </div>
          <p
            style={{
              fontSize: "2rem",
              fontWeight: 600,
              margin: "0.5rem 0 1rem",
            }}
          >
            {loading ? (
              <FiLoader
                className="animate-spin"
                style={{ verticalAlign: "middle" }}
              />
            ) : (
              formatCount(voiceCount)
            )}
          </p>
        </div>

        <div className="dashboard-card" style={{ marginBottom: 0 }}>
          <div className="card-header">
            <h3 className="card-title">Email tests</h3>
            <FiMail className="card-icon" style={{ color: "#2980b9" }} />
          </div>
          <p
            style={{
              fontSize: "2rem",
              fontWeight: 600,
              margin: "0.5rem 0 1rem",
            }}
          >
            {loading ? (
              <FiLoader
                className="animate-spin"
                style={{ verticalAlign: "middle" }}
              />
            ) : (
              formatCount(emailCount)
            )}
          </p>
        </div>
      </div>

      <h2
        style={{ fontSize: "1.15rem", color: "#2c3e50", marginBottom: "1rem" }}
      >
        Run tests
      </h2>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "1rem",
        }}
      >
        <Link
          to="/test/voice"
          className="dashboard-card"
          style={{
            marginBottom: 0,
            textDecoration: "none",
            color: "inherit",
            display: "block",
          }}
        >
          <div className="card-header">
            <h3 className="card-title">Voice call test</h3>
            <FiPhone className="card-icon" style={{ color: "#27ae60" }} />
          </div>
          <p
            style={{
              color: "#7f8c8d",
              fontSize: "0.95rem",
              margin: "0 0 1rem",
            }}
          >
            Template, language, and phone — same flow as before.
          </p>
          <span
            style={{
              color: "#27ae60",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
            }}
          >
            Open <FiArrowRight />
          </span>
        </Link>

        <Link
          to="/test/email"
          className="dashboard-card"
          style={{
            marginBottom: 0,
            textDecoration: "none",
            color: "inherit",
            display: "block",
          }}
        >
          <div className="card-header">
            <h3 className="card-title">Email test</h3>
            <FiMail className="card-icon" style={{ color: "#2980b9" }} />
          </div>
          <p
            style={{
              color: "#7f8c8d",
              fontSize: "0.95rem",
              margin: "0 0 1rem",
            }}
          >
            Same templates as voice
          </p>
          <span
            style={{
              color: "#2980b9",
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.35rem",
            }}
          >
            Open <FiArrowRight />
          </span>
        </Link>
      </div>
    </div>
  );
};

export default Dashboard;
